import { useCallback, useRef } from 'react'

type ScrollLockTarget = HTMLElement | React.RefObject<HTMLElement>

/**
 * Note: It's generally best to avoid user agent sniffing, but the issue we need
 * to work around in this hook is only present on iOS and isn't related to any
 * specific browser features that we can detect.
 */
function isIOS () {
  return /iPad|iPhone|iPod/i.test(navigator.userAgent)
}

function getElement (element: ScrollLockTarget): HTMLElement | null {
  if (element instanceof HTMLElement) {
    return element
  }

  return element.current
}

function getScrollbarGutter (element: HTMLElement) {
  if (element === document.body) {
    return window.innerWidth - document.documentElement.clientWidth
  }

  const computedStyle = getComputedStyle(element)
  const borderRightWidth = parseFloat(computedStyle.borderRightWidth)
  const borderLeftWidth = parseFloat(computedStyle.borderLeftWidth)

  // The `offsetWidth` includes the element's padding, border, and scrollbar
  // width. The `clientWidth` includes the element's padding but excludes the
  // border and scrollbar width. So by comparing the `offsetWidth` and
  // `clientWidth`, we can get the width of the scrollbar.
  return (
    element.offsetWidth -
    element.clientWidth -
    borderRightWidth -
    borderLeftWidth
  )
}

export function useScrollLock (target: ScrollLockTarget = document.body) {
  const initialElement = getElement(target)
  const originalProperties = useRef({
    overflow: initialElement?.style.overflow ?? '',
    paddingRight: initialElement?.style.paddingRight ?? '',
    position: initialElement?.style.position ?? '',
    scrollY: window.scrollY,
    top: initialElement?.style.top ?? '',
    width: initialElement?.style.width ?? '',
  })

  const lockScroll = useCallback(() => {
    const element = getElement(target)

    if (!element || element.hasAttribute('data-scroll-locked')) return

    const style = element.style
    const scrollY = window.scrollY

    originalProperties.current = {
      overflow: style.overflow,
      paddingRight: style.paddingRight,
      position: style.position,
      scrollY,
      top: style.top,
      width: style.width,
    }

    const scrollbarGutter = getScrollbarGutter(element)
    const originalPaddingRight = parseFloat(getComputedStyle(element).paddingRight)

    // Prevent scrolling
    style.overflow = 'hidden'

    // Applying `overflow: hidden` is sufficient for preventing scrolling on
    // everything _except_ the body element in iOS Webkit-based browsers 😢.
    // There are different approaches with different levels of complexity and
    // different tradeoffs to handle iOS, but we're going with a simple one for
    // now that seems to work for our use cases: applying fixed positioning to
    // the body element.
    if (isIOS() && element === document.body) {
      style.position = 'fixed'
      style.top = `-${scrollY}px`
      style.width = '100%'
    }

    // Prevent layout shifts of content in the normal flow of the document by
    // adding the scrollbar width to the right padding
    style.paddingRight = `${originalPaddingRight + scrollbarGutter}px`

    // Store the scrollbar gutter width for use by fixed/absolute positioned
    // elements to prevent layout shifts
    style.setProperty('--scrollbar-gutter', `${scrollbarGutter}px`)

    // Store the locked state in a data attribute so it can be read by this
    // and any other instance of this hook.
    element.setAttribute('data-scroll-locked', 'true')
  }, [target])

  const unlockScroll = useCallback(() => {
    const element = getElement(target)

    if (!element || !element.hasAttribute('data-scroll-locked')) return

    const style = element.style

    const { overflow, paddingRight, position, scrollY, top, width } =
      originalProperties.current

    style.overflow = overflow
    style.paddingRight = paddingRight

    if (isIOS() && element === document.body) {
      style.position = position
      style.top = top
      style.width = width
      document.documentElement.scrollTop = scrollY
    }
    style.removeProperty('--scrollbar-gutter')

    element.removeAttribute('data-scroll-locked')
  }, [target])

  return { lockScroll, unlockScroll }
}
